import React, { useState, useRef } from 'react'
import './styles/pressAndHoldButton.scss'
import classNames from 'classnames'
import { InfoBox, Pointer } from 'uiComponents/infotip'

interface PressAndHoldButtonButtonProps {
    text: string
    onComplete: () => void
    type?: 'button' | 'submit' | 'reset'
    holdTime?: number
    className?: string
    showTooltip?: boolean
    destructive?: boolean
    disabled?: boolean
}

const PressAndHoldButton = ({
    text,
    onComplete,
    type = 'button',
    holdTime = 2000,
    className,
    showTooltip = true,
    destructive = false,
    disabled = false,
}: PressAndHoldButtonButtonProps) => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    const [completed, setCompleted] = useState(false)
    const [interacted, setInteracted] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const onMouseDown = () => {
        if (disabled) return
        timeout = setTimeout(() => {
            if (buttonRef.current?.matches(':active')) {
                if (!completed) {
                    setCompleted(true)
                    onComplete()
                }
            }
        }, holdTime)
    }

    const onMouseUp = () => {
        if (disabled) return
        buttonRef.current?.blur()
        if (timeout) {
            clearTimeout(timeout)
        }
    }

    const onInteract = () => {
        if (disabled) return
        setInteracted(true)
    }

    return (
        <div className={classNames('press-and-hold-button-container', { interacted })}>
            <button
                ref={buttonRef}
                disabled={disabled}
                className={classNames('press-and-hold-button', className, { completed, destructive })}
                type={type}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onContextMenu={onMouseUp}
                onTouchStart={onMouseDown}
                onTouchCancel={onMouseUp}
                onTouchEnd={onMouseUp}
                onClick={onInteract}
                style={
                    {
                        '--hold-time': `${holdTime}ms`,
                    } as React.CSSProperties
                }
            >
                <div className="press-and-hold-button-text">{text}</div>
                <div className="press-and-hold-button-progress">
                    <div className="press-and-hold-button-text">{text}</div>
                </div>
            </button>
            {showTooltip && (
                <InfoBox pointer="left" smallBoxText className="tooltip-box">
                    <Pointer pointer="left" />
                    Press and hold to confirm
                </InfoBox>
            )}
        </div>
    )
}

export default PressAndHoldButton
